/* "אפשר לקנות את זה?": מה קנייה עושה לתזרים.
   מריצים את התחזית פעמיים, בלי הקנייה ועם הקנייה, ומשווים את הנקודה הנמוכה בכל תקופה.
   כמו במסך הבית: רק מה שידוע. בנוסף, אותה בדיקה עם ההוצאות הרגילות שעוד יגיעו (אוכל, דלק,
   ושאר העסקאות בכרטיס), כשורה "אם תוציאו כרגיל". */
import { S, memo } from './state.js';
import { fISO, todayISO, dDays, median } from '../lib/format.js';
import { curKey, keyAdd, keyOfISO, periodEnd, periodLength, monthStart } from './periods.js';
import { project, lowPoint, cashVarTypical, daysToEnd } from './forecast.js';
import { balEst } from './balance.js';
import { cardKeyOf, cardChargeDay, cardChargeISOOfKey, cardChargeAmount, cardPeriodSums, cardClosedKeys, cardCurKey } from './card.js';
import { safeToSpend } from './plan.js';

export const PAY_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10, 12, 18, 24, 36];

/** התשלומים של קנייה: באשראי ביום החיוב של כל מחזור, בעו"ש באותו יום בכל חודש */
export function purchaseEvents({ amount, date, payments = 1, src = 'credit', name = 'הקנייה' }) {
  const n = Math.max(1, Math.round(payments) || 1), total = Math.round(amount * 100) / 100;
  const each = Math.floor((total / n) * 100) / 100;
  const d = new Date(date + 'T12:00:00'), out = [];
  const k0 = src === 'credit' ? cardKeyOf(d) : null, ms = monthStart(), cd = cardChargeDay();
  for (let i = 0; i < n; i++) {
    let iso;
    if (src === 'credit') iso = cardChargeISOOfKey(keyAdd(k0, i), ms, cd);
    else {
      const dim = new Date(d.getFullYear(), d.getMonth() + i + 1, 0).getDate();
      iso = fISO(new Date(d.getFullYear(), d.getMonth() + i, Math.min(d.getDate(), dim), 12));
    }
    const a = i === n - 1 ? Math.round((total - each * (n - 1)) * 100) / 100 : each;
    out.push({ iso, amount: a, name: name + (n > 1 ? ' (תשלום ' + (i + 1) + '/' + n + ')' : '') });
  }
  return out;
}

/** מה עוד יוצא בדרך כלל עד סוף התקופה ועוד לא בתחזית: הוצאות שוטפות מהעו"ש, ושאר העסקאות בכרטיס */
export function usualExtras() {
  const out = [], today = todayISO(), k = curKey(), end = fISO(periodEnd(k));
  const cv = cashVarTypical().amount;
  if (cv > 0) {
    const perDay = cv / periodLength(k), t = new Date(today + 'T12:00:00');
    let acc = 0, n = 0;
    for (let i = 1; ; i++) {
      const d = new Date(t.getFullYear(), t.getMonth(), t.getDate() + i, 12), iso = fISO(d);
      if (iso > end) break;
      acc += perDay;
      n++;
      const last = fISO(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 12)) > end;
      if (n === 7 || last) { out.push({ iso, amount: Math.round(acc), name: 'הוצאות שוטפות רגילות מהעו"ש (הערכה)', est: true }); acc = 0; n = 0; }
    }
  }
  // הכרטיס: מה שנרשם עד עכשיו כבר בחיוב הקרוב. בדרך כלל מגיע עד סוף המחזור עוד כמה שחסר עד החציון
  const sums = cardPeriodSums(), keys = cardClosedKeys(sums), c = cardChargeAmount();
  const iso = cardChargeISOOfKey(cardCurKey(), monthStart(), cardChargeDay());
  if (keys.length >= 2 && iso > today && (!c || c.src !== 'manual')) {
    const rest = Math.round(median(keys.map((q) => sums[q])) - (c ? c.base || 0 : 0));
    if (rest > 0) out.push({ iso, amount: rest, name: 'עסקאות רגילות שעוד יגיעו לחיוב הכרטיס (הערכה)', est: true });
  }
  return out;
}

/** התחזית לתקופה הנוכחית "אם תוציאו כרגיל": אותה תחזית, ועוד ההוצאות הרגילות שעוד יגיעו עד סוף התקופה.
    המספרים הראשיים במסכים הם "רק מה שידוע". זה המספר המציאותי שלידם.
    show: יש היסטוריה, וההוצאות הרגילות מורידות את הנקודה הנמוכה לפחות ב-500 ₪ */
export function realisticToEnd() {
  if (!S.set.balDate) return null;
  return memo('realisticToEnd', () => {
    const days = daysToEnd(), now = balEst().amount, t0 = new Date(todayISO() + 'T12:00:00');
    const extra = days > 0 ? usualExtras().filter((x) => x.iso <= fISO(periodEnd(curKey()))) : [];
    const lowOf = (p) => p.reduce((m, x) => (x.balance < m.balance ? x : m), { balance: now, date: t0 });
    const pk = days > 0 ? project(days) : [], pr = days > 0 ? project(days, { extra }) : [];
    const lk = lowOf(pk), lr = lowOf(pr);
    const endK = pk.length ? pk[pk.length - 1].balance : now, endR = pr.length ? pr[pr.length - 1].balance : now;
    const cash = extra.filter((x) => !/כרטיס/.test(x.name)).reduce((t, x) => t + x.amount, 0);
    const card = extra.filter((x) => /כרטיס/.test(x.name)).reduce((t, x) => t + x.amount, 0);
    return {
      extra, cash: Math.round(cash), card: Math.round(card), sum: Math.round(cash + card),
      low: Math.round(lr.balance), lowDate: lr.date, end: Math.round(endR),
      lowKnown: Math.round(lk.balance), endKnown: Math.round(endK),
      // שורה ליד "הכי נמוך" רק כשההוצאות הרגילות באמת מורידות אותו. פער רק בסוף התקופה מופיע בהסבר
      show: extra.length > 0 && lk.balance - lr.balance >= 500,
    };
  });
}

const RANK = { no: 0, warn: 1, ok: 2 };

function judge(lb, lw) {
  const lim = Number(S.set.odLimit) || 0, floor = S.set.bufTarget > 0 ? S.set.bufTarget : 0;
  const deeper = Math.max(0, Math.round(lb.balance - lw.balance));
  let level;
  if (lw.balance >= floor || deeper < 1) level = 'ok'; // גם כשהמצב קשה בלעדיה: הקנייה לא מחמירה אותו
  else if (lim > 0 && lw.balance < -lim) level = 'no';
  else level = 'warn';
  return { level, deeper, lim, floor, intoMinus: lb.balance >= 0 && lw.balance < 0 };
}

/** ימים מהיום ועד סוף התקופה שאחרי התשלום האחרון, ולפחות עד סוף התקופה הבאה.
    קנייה מורידה את כל היתרות שאחריה, ולכן בודקים גם את הירידה הבאה אחרי התשלום האחרון.
    בלי זה, "לחכות" נראה כאילו לא מחמיר רק כי הבדיקה נגמרה ביום החיוב */
function horizon(buy) {
  const last = buy.reduce((m, x) => (x.iso > m ? x.iso : m), todayISO());
  const k = keyAdd(keyOfISO(last), 1), k2 = keyAdd(curKey(), 1);
  const end = periodEnd(k > k2 ? k : k2);
  return Math.min(400, Math.max(1, dDays(end, todayISO())));
}

/** לכל תקופה בטווח: הנקודה הנמוכה, והיתרה ביום האחרון שלה (או ביום האחרון שנבדק).
    התקופה הנוכחית מתחילה מ"בחשבון היום", כמו במסך הבית */
function byPeriod(p) {
  const today = { balance: balEst().amount, date: new Date(todayISO() + 'T12:00:00'), key: todayISO() };
  const low = { [curKey()]: today }, end = { [curKey()]: today };
  p.forEach((x) => {
    const k = keyOfISO(x.key);
    if (!low[k] || x.balance < low[k].balance) low[k] = x;
    end[k] = x;
  });
  return { low, end };
}

function runOnce(input, base = []) {
  const buy = purchaseEvents(input), days = horizon(buy);
  const pb = project(days, { extra: base }), pw = project(days, { extra: base.concat(buy) });
  const lb = lowPoint(pb, { balance: balEst().amount }), lw = lowPoint(pw, { balance: balEst().amount });
  const B = byPeriod(pb), Wp = byPeriod(pw);
  // before/after: הנקודה הנמוכה. endBefore/endAfter: היתרה בסוף התקופה, המספר שמוצג
  const periods = Object.keys(B.low).sort().map((k) => ({
    k, before: B.low[k], after: Wp.low[k] || B.low[k], endBefore: B.end[k], endAfter: Wp.end[k] || B.end[k],
  }));
  return { buy, days, lb, lw, periods, ...judge(lb, lw) };
}

/**
 * @param {{amount:number, date:string, payments?:number, src?:'credit'|'cash', name?:string}} input
 * @returns null בלי יתרה או בלי סכום
 */
export function affordCheck(input) {
  if (!S.set.balDate || !(input.amount > 0) || !input.date) return null;
  // המספרים והתשובה: "רק מה שידוע", כמו במסך הבית, כדי ש"בלי הקנייה" יהיה בדיוק המספר שבבית.
  // "אם תוציאו כרגיל" (עם ההוצאות הרגילות שעוד יגיעו) מחושב בנפרד, כשורה נוספת
  const base = usualExtras(), r = runOnce(input);
  const real = base.length ? runOnce(input, base) : null;
  // "אפשר להוציא ביום" בתקופה הנוכחית: מה שיורד בה מהקנייה יוצא מהסכום שנשאר
  const curEnd = fISO(periodEnd(curKey())), s = safeToSpend();
  const inCur = r.buy.filter((x) => x.iso <= curEnd).reduce((t, x) => t + x.amount, 0);
  const perDay = s ? { before: s.perDay, after: (s.left - inCur) / s.days } : null;
  const end = new Date(todayISO() + 'T12:00:00');
  end.setDate(end.getDate() + r.days);
  const out = { ...r, inCur, perDay, base, usualSum: Math.round(base.reduce((t, x) => t + x.amount, 0)), end, better: null, later: null,
    real: real && { lb: real.lb, lw: real.lw, level: real.level, deeper: real.deeper, periods: real.periods, buy: real.buy } };
  if (r.level === 'ok') return out;
  const improves = (x) => RANK[x.level] > RANK[r.level] || (x.level === r.level && x.deeper <= r.deeper / 2);
  // יותר תשלומים (רק באשראי)
  if ((input.src || 'credit') === 'credit') {
    for (const n of PAY_OPTIONS) {
      if (n <= (input.payments || 1)) continue;
      const x = runOnce({ ...input, payments: n });
      if (improves(x)) { out.better = { payments: n, level: x.level, low: x.lw, deeper: x.deeper, periods: x.periods, buy: x.buy }; break; }
    }
  }
  // לחכות: שבוע אחרי שבוע, עד 4 חודשים
  const d0 = new Date(input.date + 'T12:00:00');
  for (let w = 1; w <= 17; w++) {
    const date = fISO(new Date(d0.getFullYear(), d0.getMonth(), d0.getDate() + w * 7, 12));
    const x = runOnce({ ...input, date });
    if (improves(x)) { out.later = { date, level: x.level, low: x.lw, deeper: x.deeper, periods: x.periods, buy: x.buy }; break; }
  }
  return out;
}

/** התקופה הראשונה שהקנייה פוגעת בה: שם ההבדל בסוף התקופה מתחיל */
export function firstHit(r) {
  const k = keyOfISO(r.buy[0].iso);
  return r.periods.find((p) => p.k === k) || r.periods[r.periods.length - 1];
}
